import DOMPurify from "dompurify";
import { safeDecodeURI } from "../../../../../common/common_tools";
import { isClmAllowedPlugin } from "../../../../../common/clm";
import { boxGeometry, resolveMarkTarget, marksOf } from "./clmResolvers";

// Static HTML for the CLM allowlist ONLY (spec §3.5, ruling 4). Everything we
// emit is either a number we computed or text that went through escapeHtml /
// DOMPurify — never a raw user string, never inside an inline <script>.

const UNITLESS = ["opacity", "zIndex", "fontWeight", "lineHeight", "flex", "order", "zoom"];
const SANITIZE = { USE_PROFILES: { html: true } };

export function escapeHtml(text) {
  return String(text === undefined || text === null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function round(n) {
  return Math.round(n * 100) / 100;
}

export function geometryStyle(g) {
  const parts = [
    "position:absolute",
    "left:" + round(g.left) + "%",
    "top:" + round(g.top) + "%",
    "width:" + (g.width === null ? "auto" : round(g.width) + "%"),
    "height:" + (g.height === null ? "auto" : round(g.height) + "%"),
  ];
  if (g.rotation) parts.push("transform:rotate(" + round(g.rotation) + "deg)");
  return parts.join(";") + ";";
}

// Mirrors VisorBox.jsx:1043-1047 — bare numbers render as value/7 em.
export function toolbarStyleCss(style) {
  if (!style) return "";
  return Object.keys(style)
    .map((prop) => {
      const value = style[prop];
      if (value === undefined || value === null || value === "") return "";
      const css =
        typeof value === "number" && UNITLESS.indexOf(prop) === -1 ? value / 7 + "em" : value;
      // Property name lands inside style="..." unescaped by escapeHtml (which
      // only covers the value) — strip anything that isn't a legal CSS
      // property character so a quote-bearing key can't break out of the
      // attribute.
      const propName = prop
        .replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())
        .replace(/[^A-Za-z0-9-]/g, "");
      return propName + ":" + escapeHtml(css) + ";";
    })
    .join("");
}

export function imageExt(url) {
  const path = String(url || "").split(/[?#]/)[0];
  const match = /\.(png|jpe?g|webp|gif|svg)$/i.exec(path);
  return match ? match[1].toLowerCase().replace("jpeg", "jpg") : "png";
}

export function serializeImage(box, toolbar, fileName) {
  const state = toolbar.state || {};
  const scale = state.scale || 1;
  const tx = (state.translate && state.translate.x) || 0;
  const ty = (state.translate && state.translate.y) || 0;
  return (
    '<div class="clm-img" style="' +
    geometryStyle(boxGeometry(box, toolbar)) +
    toolbarStyleCss(toolbar.style) +
    '"><img src="images/' +
    escapeHtml(fileName) +
    '" alt="" style="object-fit:' +
    (state.allowDeformed === false ? "contain" : "fill") +
    ";transform:translate(" +
    round(tx) +
    "%," +
    round(ty) +
    "%) scale(" +
    round(scale) +
    ')"></div>'
  );
}

export function serializeText(box, toolbar) {
  const raw = safeDecodeURI((toolbar.state && toolbar.state.__text) || "");
  return (
    '<div class="clm-txt" style="' +
    geometryStyle(boxGeometry(box, toolbar)) +
    toolbarStyleCss(toolbar.style) +
    '">' +
    DOMPurify.sanitize(raw, SANITIZE) +
    "</div>"
  );
}

// Same five shapes as plugins/ShapeOverlay/components/ShapeOverlayRenderer.jsx.
export function serializeShape(box, toolbar) {
  const state = toolbar.state || {};
  const shape = state.shape || "rectangle";
  const color = escapeHtml(state.color || "#ef4444");
  let content = "";
  if (shape === "rectangle") {
    content =
      '<rect x="2" y="2" width="96" height="96" fill="none" stroke="' +
      color +
      '" stroke-width="3" rx="4"/>';
  } else if (shape === "circle") {
    content =
      '<circle cx="50" cy="50" r="45" fill="none" stroke="' + color + '" stroke-width="3"/>';
  } else if (shape === "arrow") {
    const id = "clm-ah-" + escapeHtml(String(box.id || "").replace(/[^A-Za-z0-9_-]/g, ""));
    const x1 = state.flipH ? 95 : 5;
    const x2 = state.flipH ? 5 : 95;
    const y1 = state.flipV ? 95 : 5;
    const y2 = state.flipV ? 5 : 95;
    content =
      '<defs><marker id="' +
      id +
      '" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">' +
      '<polygon points="0 0, 10 3, 0 6" fill="' +
      color +
      '"/></marker></defs>' +
      '<line x1="' +
      x1 +
      '" y1="' +
      y1 +
      '" x2="' +
      x2 +
      '" y2="' +
      y2 +
      '" stroke="' +
      color +
      '" stroke-width="3" marker-end="url(#' +
      id +
      ')"/>';
  } else if (shape === "highlight") {
    content =
      '<rect x="0" y="0" width="100" height="100" fill="' + color + '" fill-opacity="0.4"/>';
  } else if (shape === "blur") {
    content = '<rect x="0" y="0" width="100" height="100" fill="rgba(0,0,0,0.85)"/>';
  }
  return (
    '<div class="clm-shape" style="' +
    geometryStyle(boxGeometry(box, toolbar)) +
    '"><svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">' +
    content +
    "</svg></div>"
  );
}

// Full-document detection mirrors VisorBox.jsx:1131-1134. The document goes
// into an ATTRIBUTE (srcdoc) fully entity-escaped, so a `</script>` inside the
// artifact can never terminate anything in index.html (the known crash class).
export function serializeArtifact(box, toolbar) {
  const html = toolbar.state && toolbar.state._html;
  if (typeof html !== "string" || !html) return null;
  if (/^https?:\/\//.test(html)) return null; // external iframe: not offline-safe
  const geometry = geometryStyle(boxGeometry(box, toolbar));
  const isFullDoc = html.includes("<script") || html.includes("<html");
  if (isFullDoc) {
    return (
      '<iframe class="clm-art" style="' +
      geometry +
      '" sandbox="allow-scripts" title="" srcdoc="' +
      escapeHtml(html) +
      '"></iframe>'
    );
  }
  return (
    '<div class="clm-html" style="' +
    geometry +
    toolbarStyleCss(toolbar.style) +
    '">' +
    DOMPurify.sanitize(html, SANITIZE) +
    "</div>"
  );
}

function isNavTarget(target) {
  return target.kind === "page" || target.kind === "overlay" || target.kind === "external";
}

// Invisible, like the visor's OneClick div (plugins/OneClick/visor/OneClick.js:66-84).
export function serializeHotspot(box, toolbar, targets, boxId) {
  const nav = (targets || []).filter(isNavTarget)[0] || null;
  let onclick = "";
  if (nav && nav.kind === "page")
    onclick = "clmGo(" + JSON.stringify("#page-" + nav.pageNumber) + ")";
  else if (nav && nav.kind === "overlay") onclick = "clmOverlay(" + JSON.stringify(nav.cvId) + ")";
  else if (nav && nav.kind === "external") {
    onclick = "window.open(" + JSON.stringify(nav.url) + ",'_blank','noopener')";
  }
  return (
    '<div class="clm-hs' +
    (onclick ? "" : " clm-hs--off") +
    '" data-box="' +
    escapeHtml(boxId) +
    '" style="' +
    geometryStyle(boxGeometry(box, toolbar)) +
    '"' +
    (onclick ? ' onclick="' + escapeHtml(onclick) + '"' : "") +
    "></div>"
  );
}

// One page (or one cv overlay). boxIds is navItem.boxes / containedView.boxes —
// array order is z-order (bottom → top), so DOM order = paint order.
export function serializeContainer({ containerId, boxIds, originPageId, state, imageName }) {
  const boxesById = state.boxesById || {};
  const toolbars = state.pluginToolbarsById || {};
  const parts = [];
  const assets = [];
  const problems = [];
  const overlayIds = [];
  let imageOrdinal = 0;

  (boxIds || []).forEach((boxId) => {
    const box = boxesById[boxId];
    const toolbar = toolbars[boxId];
    if (!box || !toolbar) return;
    const pluginId = toolbar.pluginId;
    if (!isClmAllowedPlugin(pluginId)) {
      problems.push({ code: "unsupported_plugin", plugin: pluginId, boxId, sceneId: containerId });
      return;
    }
    if (pluginId === "HotspotImages" || pluginId === "Shapes") {
      const url = toolbar.state && toolbar.state.url;
      if (!url) {
        problems.push({ code: "missing_asset", plugin: pluginId, boxId, sceneId: containerId });
        return;
      }
      imageOrdinal += 1;
      const fileName = imageName(imageOrdinal, imageExt(url));
      assets.push({ fileName, url });
      parts.push(serializeImage(box, toolbar, fileName));
    } else if (pluginId === "BasicText") {
      parts.push(serializeText(box, toolbar));
    } else if (pluginId === "ShapeOverlay") {
      parts.push(serializeShape(box, toolbar));
    } else if (pluginId === "HtmlContent") {
      const html = serializeArtifact(box, toolbar);
      if (html === null) problems.push({ code: "external_html", boxId, sceneId: containerId });
      else parts.push(html);
    } else if (pluginId === "OneClick") {
      const targets = marksOf(state.marksById, boxId).map((mark) =>
        resolveMarkTarget(mark, originPageId, state.navItemsIds, state.navItemsById)
      );
      targets.forEach((target) => {
        if (target.kind === "overlay" && overlayIds.indexOf(target.cvId) === -1) {
          overlayIds.push(target.cvId);
        }
      });
      parts.push(serializeHotspot(box, toolbar, targets, boxId));
    }
  });

  return { html: parts.join("\n"), assets, problems, overlayIds };
}
